import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { CalendarDays, PlayCircle, History, ArrowRight, Ticket } from "lucide-react";

const TABS = [
  { key: "avenir", label: "À venir", icon: CalendarDays },
  { key: "replays", label: "Mes replays", icon: PlayCircle },
  { key: "historique", label: "Historique", icon: History },
];

const TYPE_LABELS = { webinaire: "Webinaire", seminaire: "Événement", congres: "Congrès" };

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function SeminarCard({ s, badge, action, clickable = true }) {
  const inner = (
    <>
      {s.visuel ? (
        <img src={s.visuel} alt="" className="h-36 w-full object-cover" loading="lazy" />
      ) : (
        <div className="h-36 bg-gradient-to-br from-cfhtb-bg to-cfhtb-light/30 flex items-center justify-center">
          <CalendarDays className="h-8 w-8 text-cfhtb-secondary/50" />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-cfhtb-secondary">{formatDate(s.date_debut)}</span>
          <span className="bg-cfhtb-bg text-cfhtb-primary px-2 py-0.5 rounded-full font-medium">
            {TYPE_LABELS[s.type] || s.type}
          </span>
          {badge}
        </div>
        <h3 className="font-serif text-lg font-semibold text-cfhtb-heading mt-2 leading-snug group-hover:text-cfhtb-primary transition-colors">
          {s.titre}
        </h3>
        {action && <div className="mt-3">{action}</div>}
      </div>
    </>
  );
  const base = "bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden flex flex-col";
  if (!clickable) {
    return (
      <div data-testid={`seminar-card-${s.slug}`} className={base}>
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={`/seminaires/${s.slug}`}
      data-testid={`seminar-card-${s.slug}`}
      className={`${base} hover:shadow-md hover:border-cfhtb-light transition-all group`}
    >
      {inner}
    </Link>
  );
}

export default function MemberSeminars() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("avenir");

  useEffect(() => {
    api("/membre/seminaires").then(setData).catch(() => {});
  }, []);

  return (
    <AppLayout>
      <div data-testid="member-seminars-page" className="space-y-6 animate-fade-in">
        <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Mes événements</h1>

        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" role="tablist">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              role="tab"
              data-testid={`seminar-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? "border-cfhtb-primary text-cfhtb-primary"
                  : "border-transparent text-cfhtb-text hover:text-cfhtb-heading"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
              {data && key === "replays" && data.replays.length > 0 && (
                <span className="bg-cfhtb-primary/10 text-cfhtb-primary text-xs px-1.5 py-0.5 rounded-full font-semibold">
                  {data.replays.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {!data ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            {tab === "avenir" && (
              data.a_venir.length === 0 ? (
                <p className="text-sm text-cfhtb-text" data-testid="seminars-empty">Aucun événement programmé pour le moment.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="seminars-upcoming-grid">
                  {data.a_venir.map((s) => (
                    <SeminarCard
                      key={s.id}
                      s={s}
                      action={s.lien_billetterie_externe && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-cfhtb-secondary">
                          <Ticket className="h-3.5 w-3.5" /> Billetterie disponible sur la fiche
                        </span>
                      )}
                    />
                  ))}
                </div>
              )
            )}

            {tab === "replays" && (
              data.replays.length === 0 ? (
                <p className="text-sm text-cfhtb-text" data-testid="replays-empty">
                  Après inscription, les replays sont disponibles dès leur mise en ligne.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="replays-grid">
                  {data.replays.map((r) => (
                    <SeminarCard
                      key={r.slug}
                      s={r}
                      badge={
                        <span className="bg-cfhtb-success/10 text-cfhtb-success px-2 py-0.5 rounded-full font-medium">
                          Replay disponible
                        </span>
                      }
                      action={
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-cfhtb-primary">
                          Regarder <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      }
                    />
                  ))}
                </div>
              )
            )}

            {tab === "historique" && (
              data.historique.length === 0 ? (
                <p className="text-sm text-cfhtb-text" data-testid="historique-empty">Aucun événement passé.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="historique-grid">
                  {data.historique.map((h, i) => (
                    <SeminarCard
                      key={`${h.slug}-${i}`}
                      s={h}
                      clickable={false}
                      badge={h.present && (
                        <span className="bg-cfhtb-turquoise/20 text-cfhtb-heading px-2 py-0.5 rounded-full font-medium">
                          Présent
                        </span>
                      )}
                    />
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
